import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Api, Http } from 'src/api';
import { authQueries, useProfileQuery } from 'src/api/queries';
import { AuthLoginRequest, AuthLoginResponse } from 'src/api/services/AuthClient';
import { Broadcast } from 'src/broadcast';
import { pushNotification } from 'src/lib/services/push-notification';
import { useUserGuiding } from 'src/lib/services/user-guiding';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { JsonWrapper } from '../JsonWrapper';
import { AuthJwtObject, jwtStorageKey } from './Auth.types';
import { AuthUtils } from './Auth.utils';
import { isAuthJwtObject } from './Auth.guards';
import { appRoutes } from 'src/routes/routes.types';
import { useBreakpoint } from 'src/lib/hooks';
import { usePreLoginAuthLocation } from 'src/routes/use-pre-login-auth-location';
import { removeFromLocalStorage } from 'src/lib/services/api-query-params/use-sorts';
import { useActivityDetection } from 'src/hooks/useActivityDetection';
import { useCrossTabSessionSync } from 'src/hooks/useCrossTabSessionSync';
import { DEFAULT_SESSION_CONFIG } from 'src/services/Session';

let timeoutId: NodeJS.Timeout | null = null;

type UseAuthOptions = {
  /**
   * Run the idle/absolute session-timeout machinery: DOM activity listeners,
   * the cross-tab BroadcastChannel and the 1s inactivity check.
   *
   * OFF BY DEFAULT, and exactly one mounted component may turn it on —
   * AuthLayout, which owns the SessionWarningModal.
   *
   * `useAuth` is a plain hook, not a context, so it runs once per consumer and
   * several instances are live at the same time (AuthLayout plus
   * AuthenticatedHeader via useLogout, plus the Login / Verify2FA pages while
   * they're mounted). When every instance ran the machinery, each opened its own
   * BroadcastChannel('admin-session') — and BroadcastChannel delivers to every
   * same-named channel except the sender, siblings in the same document
   * included. One tap therefore made the in-tab instances broadcast activity at
   * each other in an unbounded loop, re-rendering AuthLayout (and with it
   * `<Outlet />`, the whole app) until the main thread was saturated and taps
   * were being dropped mid-commit. That was the mobile unresponsiveness in
   * #2492.
   *
   * Two independent guards now hold that down: the channel is a module-level
   * singleton so no tab can talk to itself (see useCrossTabSessionSync), and
   * replayed activity never re-broadcasts (see useActivityDetection). This flag
   * is the third — it also keeps the 1s interval and the DOM listeners to a
   * single instance.
   */
  manageSession?: boolean;
};

const useAuth = ({ manageSession = false }: UseAuthOptions = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { getLocation } = usePreLoginAuthLocation();
  const location = useLocation();
  const userGuiding = useUserGuiding();

  const query = useProfileQuery({ enabled: false });

  // Dual timeout system state
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningSecondsRemaining, setWarningSecondsRemaining] = useState(
    DEFAULT_SESSION_CONFIG.warningDurationMinutes * 60
  );
  const inactivityCheckIntervalRef = useRef<number | null>(null);
  const sessionStartTimeRef = useRef<number>(Date.now());
  // Set once the idle auto-logout has been kicked off, so the 1s interval can't
  // re-enter that branch while the request is in flight or after it failed.
  const loggingOutRef = useRef(false);
  // Read by the inactivity interval so it doesn't need them as dependencies.
  const showSessionWarningRef = useRef(showSessionWarning);
  showSessionWarningRef.current = showSessionWarning;

  const sessionEnabled = manageSession && query.isSuccess;

  // Activity detection hook. Holds no state — see the perf contract in
  // useActivityDetection; nothing here may re-render on every tap.
  const { getLastActivity, triggerActivity } = useActivityDetection({
    events: DEFAULT_SESSION_CONFIG.activityEvents,
    throttleMs: DEFAULT_SESSION_CONFIG.activityThrottleMs,
    // Keyed on ownership, NOT on `sessionEnabled`. The listeners are passive and
    // cost one ref write per second at most, so the owner keeps them attached
    // for its whole lifetime — that way the timestamp is never stale when a
    // session begins. Gating them on auth state meant someone who sat on the
    // login screen longer than the idle timeout was logged straight back out on
    // the interval's first tick after signing in.
    enabled: manageSession,
    // Local DOM activity only — the single place activity leaves the tab.
    // `triggerActivity` stays silent so activity replayed from another tab can't
    // bounce back (#2492). Broadcasting is gated on the session rather than on
    // ownership: no point telling other tabs about a logged-out user.
    onActivity: (timestamp) => {
      if (sessionEnabled) {
        broadcastActivity(timestamp);
      }
    },
  });

  // Cross-tab session sync
  const { broadcastActivity, broadcastLogout, broadcastExtended } = useCrossTabSessionSync({
    enabled: sessionEnabled,
    onLogoutInOtherTab: async () => {
      await endSession();
      navigate(appRoutes.login);
    },
    onActivityInOtherTab: (timestamp) => {
      // Extend our idle window, silently. Do NOT broadcast back.
      triggerActivity(timestamp);
    },
    onExtendedInOtherTab: () => {
      setShowSessionWarning(false);
      triggerActivity();
    },
  });

  const isLoaded = query.isSuccess || query.isError,
    isLoading = query.isLoading,
    isInitialLoading = query.isInitialLoading;

  /**
   * `renewal: true` means "the token was swapped for a fresh one", NOT "the user
   * authenticated". The distinction is load-bearing: renewing a token is not
   * user activity, and treating it as such defeats both session timeouts.
   *
   * ProtectedRoute runs `refresh()` on a 60-minute interval. That interval never
   * actually fired before #2492 — every useAuth consumer re-rendered once a
   * second (the old per-second `timeSinceActivity` tick), and the effect depends
   * on `refresh`, a fresh closure each render, so it was torn down long before
   * 60 minutes elapsed. Removing that tick makes the timer live for the first
   * time. Without this flag the renewal would then propagate as activity through
   * two paths — `setJwt` fires a `storage` event that other tabs turn into
   * another `startSession`, and `broadcastActivity()` reaches them directly — so
   * two open tabs would reset each other's idle AND absolute clocks every hour,
   * forever, and an admin could leave tabs open for a week without being logged
   * out.
   */
  const startSession = async (
    jwt: string | AuthJwtObject,
    { renewal = false }: { renewal?: boolean } = {},
  ) => {
    const token = isAuthJwtObject(jwt) ? jwt : AuthUtils.make(jwt);

    AuthUtils.setJwt(token);
    Http.setBearer(token.token);
    if (document.visibilityState !== 'hidden') {
      Broadcast.user.connect(token.token);
    }
    if (pushNotification.isSupported()) {
      pushNotification.init();
    }

    // Fetch profile with timeout to prevent infinite hanging
    // Fixes ADMIN-WEB-APP-E2, ADMIN-WEB-APP-E3
    const PROFILE_FETCH_TIMEOUT = 10000; // 10 seconds
    let profileTimeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const profilePromise = queryClient.fetchQuery({ queryKey: query.queryKey, queryFn: query.queryFn });
      const timeoutPromise = new Promise<never>((_, reject) => {
        profileTimeoutId = setTimeout(() => reject(new Error('Profile fetch timeout')), PROFILE_FETCH_TIMEOUT);
      });

      await Promise.race([profilePromise, timeoutPromise]);
    } catch (error) {
      console.warn('Profile fetch failed or timed out, continuing session start:', error);
    } finally {
      if (profileTimeoutId) clearTimeout(profileTimeoutId);
    }

    // Load UserGuiding SDK and identify the admin user
    userGuiding.loadAndIdentify().catch((e) => console.error('Failed to load user guiding SDK', e));

    // A token arrived, so we're not mid-logout — let the idle branch fire again
    // if this session later goes idle (e.g. after a failed auto-logout).
    loggingOutRef.current = false;

    // Reset session tracking for dual timeout — a real authentication starts a
    // new session, a token renewal continues the existing one. See the note on
    // `renewal` above.
    if (!renewal) {
      sessionStartTimeRef.current = Date.now();
      triggerActivity();
      broadcastActivity();
    }
  };

  // `logout` is a fresh closure every render; the inactivity interval reads it
  // through this ref so it never has to be an effect dependency.
  const logoutRef = useRef<() => Promise<void>>(async () => {});

  const endSession = async () => {
    await Promise.all(
      Object.values(authQueries).map(async (queryKey) => {
        await queryClient.cancelQueries({ queryKey: [queryKey] });
        queryClient.removeQueries({ queryKey: [queryKey] });
      }),
    );

    Http.setBearer('');
    Broadcast.user.disconnect();
    userGuiding.shutdown();

    AuthUtils.removeJwt();

    // Cleanup dual timeout system
    if (inactivityCheckIntervalRef.current !== null) {
      clearInterval(inactivityCheckIntervalRef.current);
      inactivityCheckIntervalRef.current = null;
    }
    setShowSessionWarning(false);
  };

  const login = async (data: AuthLoginRequest): Promise<AuthLoginResponse> => {
    const response = await Api.auth.login(data);

    // Check if 2FA is required
    if (response.requires_2fa) {
      return response; // Return the 2FA response to handle in the component
    }

    // If we have an access token, start the session
    if (response.access_token) {
      await startSession(response.access_token);
    }

    return response;
  };

  const logout = async (): Promise<void> => {
    await Api.auth.logout();

    await endSession();
    // Reaches other tabs from any instance, session owner or not — user-
    // initiated logout runs through useLogout, which is not the owner. See
    // note 2 in useCrossTabSessionSync.
    broadcastLogout();
    try {
      removeFromLocalStorage('requests-table');
    } catch (_e) {}
  };

  logoutRef.current = logout;

  const verify2FA = async (code: string, email: string): Promise<AuthLoginResponse> => {
    try {
      const response = await Api.auth.verify2FA({ code, email });
      
      if (response.access_token) {
        await startSession(response.access_token);
      }
      
      return response;
    } catch (error: any) {
      // Always throw user-friendly message instead of backend error
      throw new Error('Invalid verification code');
    }
  };

  const resend2FA = async (email: string): Promise<{ message: string }> => {
    return await Api.auth.resend2FA({ email });
  };

  const refresh = async () => {
    if (AuthUtils.isExpired()) {
      AuthUtils.removeJwt();

      return;
    }

    await Api.auth
      .refresh()
      .then(async ({ access_token }) => {
        if (!access_token) {
          await endSession();

          return;
        }
        // Token swap only — must not count as activity (this runs on a timer).
        await startSession(access_token, { renewal: true });
      })
      .catch(async () => await endSession());
  };

  const handleStorageTokenChange = async (e: StorageEvent): Promise<void> => {
    if (e.key !== jwtStorageKey) {
      return;
    }

    if (e.oldValue && !e.newValue) {
      // Means user has logged out
      await endSession();
      try {
        removeFromLocalStorage('requests-table');
      } catch (_e) {}
      navigate(appRoutes.login);
      return;
    }

    const newJwt = JsonWrapper.toJson(e.newValue);
    if (!isAuthJwtObject(newJwt)) {
      return;
    }

    if (!e.oldValue) {
      // Means user has logged in
      await startSession(newJwt);
      navigate(getLocation(location.state?.from) ?? appRoutes.profile, {
        replace: true,
      });
      return;
    }

    if (e.oldValue && e.newValue) {
      // Means token has been refreshed, probably

      const oldJwt = JsonWrapper.toJson(e.oldValue);
      if (
        isAuthJwtObject(oldJwt) &&
        !AuthUtils.isExpired(newJwt) &&
        oldJwt.token !== newJwt.token
      ) {
        // Another tab renewed the token. Adopt it, but don't let that tab's
        // timer-driven refresh reset our idle/absolute clocks.
        await startSession(newJwt, { renewal: true });
      }
    }
  };

  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'hidden') {
      // Increased from 5 seconds to 15 minutes to prevent disconnection on short tab switches
      // This fixes the "Server Error" issue when users switch tabs for 2-3 minutes
      timeoutId = setTimeout(() => {
        Broadcast.user.disconnect();
      }, 15 * 60 * 1000); // 15 minutes

      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (!Broadcast.user.isConnected() && !AuthUtils.isExpired()) {
      Broadcast.user.connect(AuthUtils.getJwt().token);
      queryClient.invalidateQueries({
        queryKey: [authQueries.notifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.unreadNotifications],
      });
      queryClient.invalidateQueries({
        queryKey: [authQueries.requestCounts],
      });
    }
  };

  const mount = () => {
    window.addEventListener('storage', handleStorageTokenChange);
    window.addEventListener('visibilitychange', handleVisibilityChange);
  };

  const dismount = () => {
    window.removeEventListener('storage', handleStorageTokenChange);
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  // Handler for session warning modal - extend session
  const handleExtendSession = () => {
    console.log('[useAuth] Session extended by user');
    setShowSessionWarning(false);
    triggerActivity();
    broadcastExtended();
  };

  // Handler for session warning modal - logout
  const handleLogoutFromWarning = async () => {
    console.log('[useAuth] User chose to logout from warning');
    await logout();
    navigate(appRoutes.login);
  };

  // Monitor inactivity and absolute session timeout.
  //
  // Deps are intentionally just `[sessionEnabled]` so the interval is created
  // once per session: everything it reads that changes over time (last
  // activity, warning visibility, logout) comes from a ref. Previously
  // `lastActivity`, `showSessionWarning`, `logout` and `navigate` were all
  // dependencies — `logout` is a fresh closure every render, so the interval
  // was torn down and rebuilt on each one.
  useEffect(() => {
    if (!sessionEnabled) return;

    // NEITHER clock is re-stamped here, on purpose. `sessionStartTimeRef` is
    // seeded at mount — i.e. page load, which is the origin the 7-day absolute
    // cap wants. `lastActivityRef` inside useActivityDetection stays current
    // because its listeners are attached for this instance's whole lifetime
    // rather than only while authenticated — see the `enabled: manageSession`
    // note above.
    //
    // `startSession` does re-stamp both (search "Reset session tracking"), and
    // on this owner instance that is reachable — AuthLayout's mount-time
    // `refresh()` and `handleStorageTokenChange` both route through it. That's
    // pre-existing and bounded: those fire on real auth transitions, not on a
    // timer. Anything that calls `refresh()` on a schedule must NOT run on this
    // instance, or every tick would silently reset both timeouts and the
    // session could never expire.
    //
    // Re-stamping on each `sessionEnabled` transition would break the idle
    // timeout, because `query.isSuccess` is NOT stable across a login: the
    // hourly refresh() -> startSession() -> fetchQuery(profile) flips the shared
    // profile query to `error` and back on any network hiccup, and
    // `isLoaded = isSuccess || isError` means the user stays logged in
    // throughout. Restarting both clocks on every flap would let an unattended
    // tab on a flaky connection stay alive indefinitely. As written a blip only
    // pauses enforcement while it lasts; both countdowns resume where they were.

    // Check inactivity every second
    inactivityCheckIntervalRef.current = window.setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - getLastActivity();
      const timeSinceSessionStart = now - sessionStartTimeRef.current;
      const warningVisible = showSessionWarningRef.current;

      const inactivityThreshold = DEFAULT_SESSION_CONFIG.idleTimeoutMinutes * 60 * 1000;
      const warningThreshold = inactivityThreshold - (DEFAULT_SESSION_CONFIG.warningDurationMinutes * 60 * 1000);
      const absoluteThreshold = DEFAULT_SESSION_CONFIG.maxSessionDurationMinutes * 60 * 1000;

      // Check absolute session timeout (7 days)
      if (timeSinceSessionStart >= absoluteThreshold) {
        // Same re-entry guard as the idle branch below.
        if (!loggingOutRef.current) {
          loggingOutRef.current = true;
          console.log('[useAuth] Absolute session timeout reached (7 days) - logging out');
          logoutRef.current()
            .catch((e) => console.error('[useAuth] Absolute timeout logout failed:', e))
            .finally(() => navigate(appRoutes.login));
        }
        return;
      }

      // Check inactivity timeout
      if (timeSinceActivity >= inactivityThreshold) {
        // Guard + catch because this interval is now stable and re-enters every
        // second: `logout()` awaits Api.auth.logout() BEFORE endSession(), so an
        // offline tab (the likely state of an idle one) would reject, leave the
        // session standing, and hammer /logout once a second forever.
        if (!warningVisible && !loggingOutRef.current) {
          loggingOutRef.current = true;
          console.log('[useAuth] Inactivity timeout reached - auto logout');
          logoutRef.current()
            .catch((e) => console.error('[useAuth] Auto logout failed:', e))
            .finally(() => navigate(appRoutes.login));
        }
        return;
      }

      // Show warning before inactivity timeout
      if (timeSinceActivity >= warningThreshold && !warningVisible) {
        const secondsUntilLogout = Math.floor((inactivityThreshold - timeSinceActivity) / 1000);
        console.log(`[useAuth] Showing inactivity warning (${Math.floor(timeSinceActivity / 60000)} min idle)`);
        setWarningSecondsRemaining(secondsUntilLogout);
        setShowSessionWarning(true);
        return;
      }

      // The user came back while the warning was up — dismiss it. Nothing else
      // clears `showSessionWarning` on activity, so without this the branch
      // below fed the modal a countdown recomputed from a fresh activity
      // timestamp (the full idle window, e.g. "480:00"), which pinned its
      // progress bar at 100%, stopped its internal countdown ever reaching zero
      // and left the dialog blocking the app until dismissed by hand. On mobile
      // the tap or scroll used to read the dialog was enough to trigger it.
      if (warningVisible && timeSinceActivity < warningThreshold) {
        console.log('[useAuth] Activity resumed - dismissing inactivity warning');
        setShowSessionWarning(false);
        return;
      }

      // Update countdown if warning is shown
      if (warningVisible) {
        const secondsUntilLogout = Math.floor((inactivityThreshold - timeSinceActivity) / 1000);
        setWarningSecondsRemaining(Math.max(0, secondsUntilLogout));
      }
    }, 1000);

    return () => {
      if (inactivityCheckIntervalRef.current !== null) {
        clearInterval(inactivityCheckIntervalRef.current);
        inactivityCheckIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionEnabled]);

  return {
    isLoaded,
    isLoading,
    isInitialLoading,
    login,
    logout,
    verify2FA,
    resend2FA,
    refresh,
    startSession,
    endSession,
    mount,
    dismount,
    // Dual timeout system
    showSessionWarning,
    warningSecondsRemaining,
    handleExtendSession,
    handleLogoutFromWarning,
  };
};

export { useAuth };
